import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { CustomDropdown } from '../common/CustomDropdown.js';

const TablePagination = ({
  filteredAndSortedData,
  paginationInfo,
  pageSizeOptions,
  pageSizeDropdownOpen,
  onTogglePageSize,
  currentPageSizeOption,
  onPageSizeChange,
  enableSelection,
  areAllPagesSelected,
  onSelectAllPages,
  settingsButton,
  onPageChange,
}) => {
  if (!Array.isArray(filteredAndSortedData) || filteredAndSortedData.length === 0) {
    return null;
  }

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        <span className="pagination-summary">
          Showing {paginationInfo.startItem}-{paginationInfo.endItem} of {paginationInfo.totalItems} items
        </span>
        <div className="pagination-left-controls">
          <div className="page-size-selector">
            <CustomDropdown
              selected={currentPageSizeOption.display}
              options={pageSizeOptions}
              isOpen={pageSizeDropdownOpen}
              toggleDropdown={onTogglePageSize}
              onSelect={onPageSizeChange}
              className="page-size-dropdown"
            />
          </div>
          {enableSelection && filteredAndSortedData.length > 0 && (
            <button
              className="select-all-pages-button"
              onClick={onSelectAllPages}
              type="button"
            >
              {areAllPagesSelected ? 'Deselect All' : 'Select All'}
            </button>
          )}
          {settingsButton}
        </div>
      </div>

      {paginationInfo.totalPages > 1 && (
        <div className="pagination-controls">
          <button
            className="pagination-button"
            onClick={() => onPageChange(1)}
            disabled={paginationInfo.currentPage === 1}
            type="button"
          >
            First
          </button>

          <button
            className="pagination-button"
            onClick={() => onPageChange(paginationInfo.currentPage - 1)}
            disabled={paginationInfo.currentPage === 1}
            type="button"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            Previous
          </button>

          <div className="pagination-pages">
            {(() => {
              const { currentPage, totalPages } = paginationInfo;
              const pages = [];
              const maxVisiblePages = 5;

              let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

              if (endPage - startPage < maxVisiblePages - 1) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }

              if (startPage > 1) {
                pages.push(
                  <button
                    key={1}
                    className="pagination-page"
                    onClick={() => onPageChange(1)}
                    type="button"
                  >
                    1
                  </button>
                );
                if (startPage > 2) {
                  pages.push(<span key="start-ellipsis" className="pagination-ellipsis">...</span>);
                }
              }

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button
                    key={i}
                    className={`pagination-page ${i === currentPage ? 'active' : ''}`}
                    onClick={() => onPageChange(i)}
                    type="button"
                  >
                    {i}
                  </button>
                );
              }

              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pages.push(<span key="end-ellipsis" className="pagination-ellipsis">...</span>);
                }
                pages.push(
                  <button
                    key={totalPages}
                    className="pagination-page"
                    onClick={() => onPageChange(totalPages)}
                    type="button"
                  >
                    {totalPages}
                  </button>
                );
              }

              return pages;
            })()}
          </div>

          <button
            className="pagination-button"
            onClick={() => onPageChange(paginationInfo.currentPage + 1)}
            disabled={paginationInfo.currentPage === paginationInfo.totalPages}
            type="button"
          >
            Next
            <FontAwesomeIcon icon={faChevronRight} />
          </button>

          <button
            className="pagination-button"
            onClick={() => onPageChange(paginationInfo.totalPages)}
            disabled={paginationInfo.currentPage === paginationInfo.totalPages}
            type="button"
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
};

export default TablePagination;
